import { useState } from 'react';
import { Alert, Button, Input, Modal, useToast, v } from '@kss/ui';
import { resolveTenantFromHostname } from '@kss/auth';
import { authService } from '@kss/api';

export interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  /** Switch back to the sign-in modal. */
  onSwitchToLogin?: () => void;
  /** Optional initial email (e.g. carried over from the login modal). */
  initialEmail?: string;
}

/**
 * "Forgot password?" modal — collects the customer's email and posts
 * to `/api/auth/forgot-password`. The backend takes care of:
 *
 *   1. Verifying the email is on file (silently — we always show the
 *      same generic success message either way to avoid leaking
 *      account-existence).
 *   2. Generating a single-use reset token + emailing a link to
 *      `/reset-password?token=...`.
 *
 * The frontend doesn't need a `redirectUrl` field on the request —
 * the backend embeds the reset URL based on its configured base URL
 * for the tenant.
 */
export function ForgotPasswordModal({
  open,
  onClose,
  onSwitchToLogin,
  initialEmail,
}: ForgotPasswordModalProps) {
  const toast = useToast();

  const [tenantCode, setTenantCode] = useState(
    () => resolveTenantFromHostname() ?? 'sso',
  );
  const [email, setEmail] = useState(initialEmail ?? '');
  const [emailTouched, setEmailTouched] = useState(false);
  /* Shared rule, replacing a local regex that accepted 'a@b.c'. */
  const emailError = v.required('Email')(email) ?? v.email()(email);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once the request is accepted we swap the form for a confirmation
  // panel — re-submitting from the same modal would just trigger
  // duplicate emails, which providers throttle hard.
  const [sent, setSent] = useState(false);

  function reset() {
    setError(null);
    setSent(false);
    setSubmitting(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEmailTouched(true);
    if (emailError) return;
    setSubmitting(true);
    try {
      await authService.forgotPassword({
        tenantCode: tenantCode.trim() || undefined,
        email: email.trim(),
      });
      setSent(true);
      toast.success('Check your inbox', `We sent reset instructions to ${email.trim()}.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not send reset email.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Reset your password"
      width={440}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {onSwitchToLogin ? (
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
                onSwitchToLogin();
              }}
              disabled={submitting}
              className="text-xs text-content-secondary hover:text-content-primary disabled:opacity-50"
            >
              Back to <span className="font-semibold text-primary">Sign in</span>
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={submitting}
            >
              {sent ? 'Close' : 'Cancel'}
            </Button>
            {!sent && (
              <Button onClick={handleSubmit} disabled={submitting || !!emailError}>
                {submitting ? 'Sending…' : 'Send reset link'}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {sent ? (
        <div className="grid gap-3">
          <Alert variant="success">
            If an account exists for <strong>{email}</strong>, a password-reset
            link has been emailed. The link expires in 30 minutes.
          </Alert>
          <p className="text-xs text-content-secondary">
            Didn’t get an email? Check your spam folder, then try again from
            the sign-in screen.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="grid gap-3">
          {error && <Alert variant="danger">{error}</Alert>}
          <p className="text-sm text-content-secondary">
            Enter your account email and we’ll send you a link to choose a
            new password.
          </p>
          <Input
            label="Company name"
            value={tenantCode}
            onChange={(e) => setTenantCode(e.target.value)}
            placeholder="sso"
            autoComplete="organization"
            hint="Pre-filled from your subdomain — change to switch tenants."
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            error={emailTouched ? emailError ?? undefined : undefined}
            placeholder="you@company.com"
            autoComplete="email"
            autoFocus
          />
          <button type="submit" hidden />
        </form>
      )}
    </Modal>
  );
}
